import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  alterarVertical, campanhaEditavel, FORMATO_DE_ORIGEM, MOTIVOS_DE_PARADA,
  procurarNoParque, textoDoEncerramento, verVertical,
  type AlteracaoDaVertical, type CampanhaCompleta, type EquipamentoDoParque, type MotivoDeParada,
  type VerticalCompleta, type VerticalDaCampanha,
} from '../../api/cptu';
import { ErroApi } from '../../api';
import {
  coordenada, dataDoDia, dataHora, faixaProfundidade, numero, paraCampo, paraNumero, plural,
} from '../../formato';
import { useSessao } from '../../sessao';
import { degrauDe, SITUACAO_DA_VERTICAL_CPTU } from '../../situacoes';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../../TabelaComFicha';
import {
  Area, Aviso, Campo, CampoNumerico, Carregando, Dialogo, ErroDaApi, SeloDaEscada, useEstreito, Vazio,
} from '../../ui';
import { AcaoSecundariaBloqueada, motivoDaCampanhaParada, motivoDoBloqueio } from './CampanhaMoldura';
import { EdicaoRapidaDasVerticais } from './EdicaoRapidaDasVerticais';
import {
  avisoDoCone, FUSOS, fusoDePartida, lerUtm, rascunhoDe, textoDoCone, textoDoUtm, utmGravado,
  type ModoDaCoordenada, type RascunhoDaVertical, type SistemaDaCoordenada,
} from './edicaoRapida';
import { FormularioDaJanela } from './FormularioDaJanela';
import { FotosDaVertical } from './FotosDaVertical';
import { rotaDaVertical } from './interpretacao';

/**
 * A aba Verticais da campanha CPTu — onda 312, Tarefa 312.5 (RN-253).
 *
 * A tabela com ficha (P-LST-07): cada linha é uma vertical ativa, e a ficha
 * à direita traz o cabeçalho que o engenheiro conferiu na importação, a
 * vertical que esta substituiu (R8) e as fotos do equipamento no ponto. O
 * cabeçalho (NA, cota, coordenada, cone e encerramento) muda numa janela com
 * motivo — o bruto não muda nunca: as leituras e o arquivo ficam como
 * entraram, e o que muda é premissa auditada.
 *
 * No celular a campanha é consulta: as colunas de detalhe saem (elas vão
 * para a ficha), e a ficha cobre a lista. As fotos, sim, entram pelo celular
 * — é ele que está no ponto, com a câmera.
 *
 * Onda 313 (R3): a tabela é a porta da interpretação — cada linha e a ficha
 * têm «Interpretar», que abre a vertical (`/cptu/:id/verticais/:vid`); e a
 * tela da vertical volta aqui pelo «Fotos (n)», com a ficha dela aberta
 * (`?ficha=<vertical>`).
 */
export function CampanhaVerticais({ c, gravado, aoMudar }: {
  c: CampanhaCompleta; gravado: string | null; aoMudar(): Promise<void>;
}) {
  const { pode, eu } = useSessao();
  const estreito = useEstreito();
  const bloqueio = !pode('cptu.editar') ? 'permissao' : !campanhaEditavel(c.campanha.situacao) ? 'parada' : null;
  const editavel = !bloqueio;
  const cancelada = c.campanha.situacao === 'cancelada';
  /* Onda 333: por que a vertical não muda — a frase da ação bloqueada da casa (P-UI-24), sem o código da permissão.
     Na campanha cancelada, a frase dela (revisão da 333): as ações ficam à vista e bloqueadas, como nas outras paradas. */
  const motivoBloqueado = cancelada ? motivoDaCampanhaParada('cancelada')
    : bloqueio
      ? motivoDoBloqueio(bloqueio, 'não muda as verticais da campanha CPTu', c.campanha.situacao, eu?.atuandoPor ? eu.nome : null)
      : null;
  const [busca] = useSearchParams();
  const fichaPedida = busca.get('ficha');
  const colunas = useMemo(() => colunasDa(c.campanha.id), [c.campanha.id]);
  const ativas = useMemo(() => c.verticais.filter((v) => v.situacao === 'ativa'), [c.verticais]);
  /* Na cancelada, o aviso das coordenadas não chama para uma mudança que ela não recebe. */
  const semCoordenada = cancelada ? 0 : ativas.filter((v) => v.latitude === null || v.longitude === null).length;
  /** Há o que editar na grade: as verticais ativas (na cancelada, as que foram canceladas com ela, à vista e bloqueadas). */
  const comEdicaoRapida = !estreito && (cancelada ? c.verticais.length > 0 : ativas.length > 0);
  /** A Edição rápida (onda 333): os rascunhos, por id da vertical ativa; null = fora da edição. */
  const [rascunhos, setRascunhos] = useState<Record<string, RascunhoDaVertical> | null>(null);
  /** O sistema das coordenadas da grade (onda 334): UTM no fuso das verticais, como a Edição rápida das amostras abre. */
  const [sistema, setSistema] = useState<SistemaDaCoordenada>({ modo: 'utm', fuso: 22 });
  const [gravouAgora, setGravouAgora] = useState<string | null>(null);
  const editando = !!rascunhos && editavel;

  return (
    <>
      {gravado && !gravouAgora && <Aviso tipo="ok" titulo="Gravado.">{gravado}</Aviso>}
      {gravouAgora && <Aviso tipo="ok" titulo="Gravado.">{gravouAgora}</Aviso>}
      {c.verticais.length === 0 ? (
        <section className="cartao">
          <Vazio>
            Nenhuma vertical nesta campanha ainda. A vertical entra pela importação do arquivo do
            equipamento — a planilha do Pagani, um CSV/TXT ou o projeto do CPeT-IT —, no computador.
          </Vazio>
        </section>
      ) : editando && !estreito ? (
        <EdicaoRapidaDasVerticais c={c} verticais={ativas} rascunhos={rascunhos}
          aoMudarRascunhos={setRascunhos}
          sistema={sistema} aoMudarSistema={setSistema}
          aoSair={() => setRascunhos(null)}
          aoGravado={async (n) => {
            setRascunhos(null);
            setGravouAgora(`${plural(n, 'vertical alterada', 'verticais alteradas')} pela Edição rápida; `
              + 'o NA, a cota e a coordenada foram também aos pontos da OS.');
            await aoMudar();
          }} />
      ) : (
        <section className="cartao">
          {(semCoordenada > 0 || comEdicaoRapida) && (
            <div className="cptu-barra-das-verticais">
              {semCoordenada > 0 ? (
                <p className="cptu-faltam-coordenadas">
                  <span aria-hidden="true">▲ </span>
                  {semCoordenada === 1 ? 'Falta a coordenada em 1 vertical' : `Faltam coordenadas em ${plural(semCoordenada, 'vertical', 'verticais')}`}
                  {' '}— a análise espacial e o croqui precisam da coordenada.
                </p>
              ) : <span />}
              {comEdicaoRapida && (
                editavel ? (
                  <button type="button" className="botao secundario"
                    onClick={() => {
                      setGravouAgora(null);
                      const fuso = fusoDePartida(ativas);
                      setSistema({ modo: 'utm', fuso });
                      setRascunhos(Object.fromEntries(ativas.map((v) => [v.id, rascunhoDe(v, fuso)])));
                    }}>
                    Edição rápida
                  </button>
                ) : motivoBloqueado ? (
                  <AcaoSecundariaBloqueada texto="Edição rápida" motivo={motivoBloqueado} />
                ) : null
              )}
            </div>
          )}
          <TabelaComFicha
            linhas={c.verticais}
            chave={(v) => v.id}
            nome={(v) => `vertical ${v.codigo}`}
            rotulo="Verticais da campanha"
            colunas={colunas}
            ficha={(v) => fichaDaVertical(c.campanha.id, v, editavel, motivoBloqueado, aoMudar)}
            inicial={fichaPedida && c.verticais.some((v) => v.id === fichaPedida) ? fichaPedida : null}
          />
          <p className="cptu-nota-da-tabela">
            {c.campanha.situacao === 'cancelada'
              ? plural(c.verticais.length, 'vertical cancelada com a campanha', 'verticais canceladas com a campanha')
              : plural(c.verticais.length, 'vertical ativa', 'verticais ativas')}
            {c.substituidas > 0 && ` · ${plural(c.substituidas, 'importação substituída', 'importações substituídas')} no histórico`}
            {' · na ordem do código'}
          </p>
        </section>
      )}
    </>
  );
}

const m2 = (v: number | null | undefined) => (v === null || v === undefined ? null : `${numero(v, 2)} m`);

/** As colunas; a primeira célula leva o «Interpretar» da linha (um link: não abre a ficha por baixo). */
const colunasDa = (campanhaId: string): ColunaComFicha<VerticalDaCampanha>[] => [
  {
    chave: 'vertical',
    cabeca: <th scope="col">Vertical</th>,
    celula: (v) => (
      <th scope="row">
        <span className="mono cptu-codigo">{v.codigo}</span>
        {v.data_ensaio && <span className="sub no-estreito">{dataDoDia(v.data_ensaio)}</span>}
        <Link className="cptu-interpretar" to={rotaDaVertical(campanhaId, v.id)}
          aria-label={`Interpretar a vertical ${v.codigo}`}>Interpretar</Link>
      </th>
    ),
  },
  {
    chave: 'data', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">Data</th>,
    celula: (v) => (
      <td className="col-opcional mono">{v.data_ensaio ? dataDoDia(v.data_ensaio) : <span className="nada">—</span>}</td>
    ),
  },
  {
    chave: 'profundidade',
    cabeca: <th scope="col" className="n">Profundidade final</th>,
    celula: (v) => <td className="n mono">{m2(v.profundidade_final) ?? <span className="nada">—</span>}</td>,
  },
  {
    chave: 'leituras', detalhe: true,
    cabeca: <th scope="col" className="n col-opcional">Leituras</th>,
    celula: (v) => <td className="n mono col-opcional">{numero(v.n_leituras, 0)}</td>,
  },
  {
    chave: 'na',
    cabeca: <th scope="col" className="n">NA</th>,
    celula: (v) => (
      <td className="n mono">{m2(v.nivel_agua) ?? <span className="nada">não informado</span>}</td>
    ),
  },
  {
    chave: 'cota', detalhe: true,
    cabeca: <th scope="col" className="n col-opcional">Cota</th>,
    celula: (v) => <td className="n mono col-opcional">{m2(v.cota) ?? <span className="nada">—</span>}</td>,
  },
  {
    chave: 'coordenada', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">Coordenada</th>,
    celula: (v) => (
      <td className="col-opcional mono cptu-coordenada">
        {coordenada(v.latitude, v.longitude) || <span className="nada">—</span>}
      </td>
    ),
  },
  {
    chave: 'cone', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">Cone</th>,
    celula: (v) => (
      <td className="col-opcional">
        {/* Onda 335: o achado pelo número do arquivo aparece como o ligado, com a origem dita embaixo. */}
        {v.cone_equipamento_id
          ? <span className="mono">{v.cone_patrimonio}</span>
          : v.cone_pelo_numero
            ? <><span className="mono">{v.cone_patrimonio}</span><span className="sub">achado pelo número do arquivo</span></>
            : v.cone_codigo
              ? <><span className="mono">{v.cone_codigo}</span>
                <span className="sub">{v.cone_numero_ambiguo ? 'casa com mais de um equipamento' : 'fora de Equipamentos'}</span></>
              : <span className="nada">—</span>}
      </td>
    ),
  },
  {
    chave: 'encerramento', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">Encerramento</th>,
    celula: (v) => (
      <td className="col-opcional">
        {textoDoEncerramento(v.motivo_parada, v.motivo_parada_texto) ?? <span className="nada">—</span>}
      </td>
    ),
  },
  {
    chave: 'fotos', detalhe: true,
    cabeca: <th scope="col" className="n col-opcional">Fotos</th>,
    celula: (v) => (
      <td className="n col-opcional">{v.fotos.length ? v.fotos.length : <span className="nada">sem foto</span>}</td>
    ),
  },
];

function fichaDaVertical(
  campanhaId: string, v: VerticalDaCampanha, editavel: boolean, motivoBloqueado: string | null,
  aoMudar: () => Promise<void>,
): FichaDaLinha {
  return {
    rotulo: 'Vertical',
    titulo: v.codigo,
    tituloCodigo: true,
    sub: (
      <>
        <span>{v.data_ensaio ? `Ensaio em ${dataDoDia(v.data_ensaio)}` : 'Sem data do ensaio'}</span>
        <span className="mono">{v.arquivo}</span>
      </>
    ),
    corpo: <FichaDaVertical key={v.id} id={v.id} editavel={editavel} motivoBloqueado={motivoBloqueado} aoMudar={aoMudar} />,
    acoes: <Link className="botao" to={rotaDaVertical(campanhaId, v.id)}>Interpretar</Link>,
  };
}

/** A ficha de uma vertical: o cabeçalho, a vertical que ela substituiu, as fotos e o histórico do ponto. */
function FichaDaVertical({ id, editavel, motivoBloqueado, aoMudar }: {
  id: string; editavel: boolean; motivoBloqueado: string | null; aoMudar(): Promise<void>;
}) {
  const estreito = useEstreito();
  const [v, setV] = useState<VerticalCompleta | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [editando, setEditando] = useState(false);

  const carregar = useCallback(async () => {
    try { setV(await verVertical(id)); setErro(null); } catch (e) { setErro(e); }
  }, [id]);
  useEffect(() => { void carregar(); }, [carregar]);

  if (erro && !v) return <ErroDaApi erro={erro} />;
  if (!v) return <Carregando texto="Abrindo a vertical" />;

  const mudou = async (nova: VerticalCompleta) => { setV(nova); await aoMudar(); };
  const r = v.resumo;
  const inclinacao = r?.inclinacao_maxima != null ? Number(r.inclinacao_maxima) : null;
  const ini = r?.profundidade_inicial != null ? Number(r.profundidade_inicial) : null;
  const fim = r?.profundidade_final != null ? Number(r.profundidade_final) : null;
  const anteriores = v.historico.filter((h) => h.id !== v.id);

  return (
    <>
      {/* A recarga que falha depois da primeira aparece: a ficha pode estar velha. */}
      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="tf-selos">
        <SeloDaEscada degrau={degrauDe(SITUACAO_DA_VERTICAL_CPTU, v.situacao)} />
        <span className="selo neutro">{FORMATO_DE_ORIGEM[v.formato_origem] ?? v.formato_origem}</span>
      </div>
      {v.substitui && (
        <div className="tf-aviso">
          <strong>Substitui a importação de {dataHora(v.substitui.importada_em)}.</strong>
          {v.substitui.motivo && <> Motivo: {v.substitui.motivo}</>}
        </div>
      )}
      <CamposDaFicha itens={[
        ['Ponto da OS', <span className="mono">{v.ponto.codigo}</span>],
        ['Data do ensaio', v.data_ensaio ? dataDoDia(v.data_ensaio) : 'não informada'],
        ['Leituras', <>
          {numero(v.n_leituras, 0)}
          {r && r.sem_leitura > 0 && <span className="sub">{plural(r.sem_leitura, 'sem leitura (o motor a tira)', 'sem leitura (o motor as tira)')}</span>}
        </>],
        ['Profundidade', ini !== null && fim !== null ? `${faixaProfundidade(ini, fim)} m` : m2(v.profundidade_final)],
        ['NA', m2(v.nivel_agua) ?? 'não informado'],
        ['Cota', m2(v.cota)],
        ['Coordenada', coordenada(v.latitude, v.longitude)
          ? <span className="mono">{coordenada(v.latitude, v.longitude)}</span> : null],
        /* Onda 334: a mesma coordenada em UTM (SIRGAS 2000), no fuso da longitude, como na ficha da amostra. */
        ['Coordenada UTM', textoDoUtm(v.utm_e, v.utm_n, v.utm_fuso)
          ? <span className="mono">{textoDoUtm(v.utm_e, v.utm_n, v.utm_fuso)}</span> : null],
        ['Cone', avisoDoCone(v) ? <Aviso tipo="atencao">{avisoDoCone(v)}</Aviso> : textoDoCone(v)],
        ['Encerramento', textoDoEncerramento(v.motivo_parada, v.motivo_parada_texto)],
        ['Dissipações', v.dissipacoes ? String(v.dissipacoes) : null],
        ['Inclinação máxima', inclinacao !== null && Number.isFinite(inclinacao) ? `${numero(inclinacao, 2)}°` : null],
        ['u₂ negativo', r && r.u2_negativo > 0 ? plural(r.u2_negativo, 'leitura', 'leituras') : null],
        ['Importada', <>{v.importada_por_nome}<span className="sub">{dataHora(v.importada_em)}</span></>],
      ]} />
      {editavel && !estreito && v.situacao === 'ativa' && (
        <button type="button" className="botao secundario" onClick={() => setEditando(true)}>
          Editar o cabeçalho
        </button>
      )}
      {/* Onda 333: à vista e bloqueado, com o motivo (P-UI-24) — antes, o botão sumia sem dizer por quê. */}
      {!editavel && !estreito && motivoBloqueado && (v.situacao === 'ativa' || v.situacao === 'cancelada') && (
        <AcaoSecundariaBloqueada texto="Editar o cabeçalho" motivo={motivoBloqueado} />
      )}
      <FotosDaVertical vertical={v} editavel={editavel} aoMudar={mudou} />
      {anteriores.length > 0 && (
        <div className="cptu-historico-do-ponto">
          <h3>Importações anteriores deste ponto</h3>
          <ul className="tf-lista">
            {anteriores.map((h) => (
              <li key={h.id}>
                <span className="mono">{h.arquivo}</span>
                <span className="sub">
                  {dataHora(h.importada_em)} · {h.importada_por_nome} · {numero(h.n_leituras, 0)}{' '}
                  {h.n_leituras === 1 ? 'leitura' : 'leituras'}
                  {h.motivo_substituicao && ` · substituída: ${h.motivo_substituicao}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {editando && (
        <JanelaDaVertical v={v} aoFechar={() => setEditando(false)}
          aoPronto={async (nova) => { setEditando(false); await mudou(nova); }} />
      )}
    </>
  );
}

/**
 * O cabeçalho da vertical numa janela com motivo: NA, cota, coordenada, cone
 * e encerramento. O NA, a cota e a coordenada vão também ao ponto da OS (R7).
 * Só o que mudou vai à API. O foco abre no NA, e o Enter num campo grava (com
 * o que falta dito, como no clique).
 */
function JanelaDaVertical({ v, aoFechar, aoPronto }: {
  v: VerticalCompleta; aoFechar(): void; aoPronto(nova: VerticalCompleta): Promise<void>;
}) {
  const [na, setNa] = useState(paraCampo(v.nivel_agua, 2));
  const [cota, setCota] = useState(paraCampo(v.cota, 3));
  const [lat, setLat] = useState(paraCampo(v.latitude, 7));
  const [lon, setLon] = useState(paraCampo(v.longitude, 7));
  /* Onda 334: a coordenada também em UTM (SIRGAS 2000), como a amostra avulsa — abre em UTM, no fuso dela. */
  const [modo, setModo] = useState<ModoDaCoordenada>('utm');
  const [fuso, setFuso] = useState(v.utm_fuso !== null && v.utm_fuso >= 18 && v.utm_fuso <= 25 ? v.utm_fuso : 22);
  const gravadoNoFuso = utmGravado(v, fuso);
  const [e, setE] = useState(gravadoNoFuso ? paraCampo(gravadoNoFuso.e, 2) : '');
  const [n, setN] = useState(gravadoNoFuso ? paraCampo(gravadoNoFuso.n, 2) : '');
  const trocarFuso = (f: number) => {
    const g = utmGravado(v, f);
    setFuso(f); setE(g ? paraCampo(g.e, 2) : ''); setN(g ? paraCampo(g.n, 2) : '');
  };
  const [cone, setCone] = useState<{ id: string | null; rotulo: string | null }>({
    id: v.cone_equipamento_id, rotulo: v.cone_equipamento_id ? textoDoCone(v) : null,
  });
  const [parada, setParada] = useState<MotivoDeParada | ''>(v.motivo_parada ?? '');
  const [paradaTexto, setParadaTexto] = useState(v.motivo_parada_texto ?? '');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  const numeroOuNulo = (t: string) => (t.trim() === '' ? null : paraNumero(t) ?? NaN);
  const corpo: AlteracaoDaVertical = { motivo: motivo.trim() };
  const vNa = numeroOuNulo(na);
  const vCota = numeroOuNulo(cota);
  const vLat = numeroOuNulo(lat);
  const vLon = numeroOuNulo(lon);
  if (vNa !== v.nivel_agua) corpo.nivelAgua = vNa;
  if (vCota !== v.cota) corpo.cota = vCota;
  const ue = lerUtm(e, 'e');
  const un = lerUtm(n, 'n');
  if (modo === 'graus') {
    if (vLat !== v.latitude || vLon !== v.longitude) { corpo.latitude = vLat; corpo.longitude = vLon; }
  } else if (ue.valor != null && un.valor != null
    && (gravadoNoFuso === null || ue.valor !== gravadoNoFuso.e || un.valor !== gravadoNoFuso.n)) {
    corpo.utmE = ue.valor; corpo.utmN = un.valor; corpo.utmFuso = fuso;
  } else if (ue.valor === null && un.valor === null && gravadoNoFuso !== null) {
    corpo.latitude = null; corpo.longitude = null;
  }
  if (cone.id !== v.cone_equipamento_id) corpo.coneEquipamentoId = cone.id;
  if ((parada || null) !== v.motivo_parada) corpo.motivoParada = parada || null;
  if ((paradaTexto.trim() || null) !== (v.motivo_parada_texto ?? null)) corpo.motivoParadaTexto = paradaTexto.trim() || null;

  const invalido = [vNa, vCota, ...(modo === 'graus' ? [vLat, vLon] : [])].some((x) => x !== null && !Number.isFinite(x));
  const erroDoUtm = modo === 'utm' ? ue.erro ?? un.erro ?? null : null;
  const meiaCoordenada = modo === 'graus' ? (vLat === null) !== (vLon === null)
    : !erroDoUtm && (ue.valor === null) !== (un.valor === null);
  const semTextoDoOutro = parada === 'outro' && !paradaTexto.trim();
  const nadaMudou = Object.keys(corpo).length === 1;
  const curto = motivo.trim().length < 10;
  const falta = invalido ? 'Há um número que não se lê: use vírgula ou ponto para as casas.'
    : erroDoUtm ? erroDoUtm
    : meiaCoordenada ? (modo === 'utm' ? 'O E e o N andam juntos: os dois, ou nenhum.'
      : 'A coordenada vem inteira (latitude e longitude) ou não vem.')
      : semTextoDoOutro ? 'Com o encerramento «outro motivo», escreva qual.'
        : nadaMudou ? 'Nada mudou ainda.'
          : curto ? 'Diga o motivo da alteração (pelo menos 10 caracteres).' : null;

  const gravar = async () => {
    if (falta || ocupado) return;
    setOcupado(true); setErro(null);
    try { await aoPronto(await alterarVertical(v.id, corpo)); }
    catch (e) { setErro(e); setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Cabeçalho da vertical" rotulo={`Vertical ${v.codigo}`} aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          As leituras e o arquivo do equipamento não mudam. O NA, a cota e a coordenada vão também ao
          ponto {v.ponto.codigo} da OS.
        </p>
        <div className="linha cptu-numeros">
          <CampoNumerico rotulo="NA (m)" value={na} onChange={(e) => setNa(e.target.value)}
            dica="A profundidade do nível d'água." />
          <CampoNumerico rotulo="Cota (m)" value={cota} onChange={(e) => setCota(e.target.value)} />
        </div>
        <fieldset className="cptu-coordenada-da-janela">
          <legend>Coordenada</legend>
          <div className="seg" role="group" aria-label="Sistema da coordenada">
            <button type="button" aria-pressed={modo === 'utm'} onClick={() => setModo('utm')}>UTM (SIRGAS 2000)</button>
            <button type="button" aria-pressed={modo === 'graus'} onClick={() => setModo('graus')}>Graus decimais</button>
          </div>
          {modo === 'utm' ? (
            <div className="linha cptu-numeros">
              <CampoNumerico rotulo="E (m)" value={e} onChange={(x) => setE(x.target.value)}
                dica={!gravadoNoFuso && v.latitude !== null && v.utm_fuso !== null
                  ? `A coordenada gravada está no fuso ${v.utm_fuso}S.` : 'Em metros, como 694.123,45'} />
              <CampoNumerico rotulo="N (m)" value={n} onChange={(x) => setN(x.target.value)} />
              <label className="campo">
                <span className="rotulo">Fuso</span>
                <select value={fuso} onChange={(x) => trocarFuso(Number(x.target.value))}>
                  {FUSOS.map((f) => <option key={f} value={f}>{f}S</option>)}
                </select>
              </label>
            </div>
          ) : (
            <div className="linha cptu-numeros">
              <CampoNumerico rotulo="Latitude" value={lat} onChange={(x) => setLat(x.target.value)}
                dica="Graus decimais, como -27,4936" />
              <CampoNumerico rotulo="Longitude" value={lon} onChange={(x) => setLon(x.target.value)} />
            </div>
          )}
        </fieldset>
        <EscolhaDoCone valor={cone.id} rotuloAtual={cone.rotulo} sugestao={v.cone_codigo}
          achado={v.cone_pelo_numero ? textoDoCone(v) : null} aviso={avisoDoCone(v)}
          aoEscolher={(id, rotulo) => setCone({ id, rotulo })} />
        <label className="campo">
          <span className="rotulo">Encerramento</span>
          <select value={parada} onChange={(e) => setParada(e.target.value as MotivoDeParada | '')}>
            <option value="">Não informado</option>
            {MOTIVOS_DE_PARADA.map(([m, t]) => <option key={m} value={m}>{t}</option>)}
          </select>
        </label>
        {parada === 'outro' && (
          <Campo rotulo="Qual motivo" value={paradaTexto} maxLength={500}
            onChange={(e) => setParadaTexto(e.target.value)} />
        )}
        <Area rotulo="Motivo da alteração" rows={2} value={motivo} maxLength={2000}
          dica="Vai para a trilha da vertical." onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="submit" className="botao" disabled={!!falta || ocupado}>
            {ocupado ? 'Gravando…' : 'Gravar'}
          </button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}

/**
 * O cone, escolhido no parque (Equipamentos): a rastreabilidade da
 * calibração do ensaio. Procura pelo código, nome ou número de série — o
 * número do arquivo (o «Probe Code» do Pagani) vem como sugestão.
 *
 * Onda 335: sem cone gravado, o que o número do arquivo achou no parque
 * (`achado`) aparece no lugar de «nenhum cone do parque», com «Trocar» —
 * não parece uma escolha obrigatória; e o número que o parque não conhece
 * avisa (`aviso`), sem impedir nada.
 */
export function EscolhaDoCone({ valor, rotuloAtual, sugestao, achado, aviso, aoEscolher }: {
  valor: string | null;
  rotuloAtual: string | null;
  sugestao?: string | null;
  achado?: string | null;
  aviso?: string | null;
  aoEscolher(id: string | null, rotulo: string | null): void;
}) {
  const [busca, setBusca] = useState(sugestao ?? '');
  const [achados, setAchados] = useState<EquipamentoDoParque[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aberta, setAberta] = useState(false);

  useEffect(() => {
    if (!aberta || busca.trim().length < 2) { setAchados(null); return undefined; }
    let vivo = true;
    const t = setTimeout(() => {
      procurarNoParque(busca.trim())
        .then((r) => { if (vivo) { setAchados(r.slice(0, 8)); setErro(null); } })
        .catch((e) => {
          if (!vivo) return;
          setAchados([]);
          setErro(e instanceof ErroApi && e.status === 403
            ? 'A lista do parque não está ao seu alcance (Equipamentos): peça a quem administra os usuários.'
            : 'Não consegui procurar no parque agora.');
        });
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca, aberta]);

  return (
    <div className="campo cptu-cone">
      <span className="rotulo">Cone (Equipamentos)</span>
      <div className="cptu-cone-atual">
        <span>{valor ? rotuloAtual ?? 'escolhido' : achado ?? 'nenhum cone do parque'}</span>
        {!aberta && (
          <button type="button" className="ligacao" onClick={() => setAberta(true)}>
            {valor || achado ? 'Trocar' : 'Escolher'}
          </button>
        )}
        {valor && (
          <button type="button" className="ligacao" onClick={() => aoEscolher(null, null)}>Tirar</button>
        )}
      </div>
      {!valor && !achado && aviso && <Aviso tipo="atencao">{aviso}</Aviso>}
      {aberta && (
        <div className="cptu-cone-busca">
          {/* O Enter na busca não grava a janela do cabeçalho (o formulário dela): ele só procura. */}
          <input type="search" value={busca} aria-label="Procurar o cone no parque"
            placeholder="Código, nome ou nº de série" onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
          {erro && <span className="erro">{erro}</span>}
          {achados && achados.length === 0 && !erro && <span className="dica">Nada no parque com «{busca}».</span>}
          {achados && achados.length > 0 && (
            <ul className="cptu-cone-achados">
              {achados.map((e) => {
                const rotulo = [e.codigo_patrimonial, e.nome, e.numero_serie ? `nº ${e.numero_serie}` : null]
                  .filter(Boolean).join(' · ');
                return (
                  <li key={e.id}>
                    <button type="button" className="ligacao"
                      onClick={() => { aoEscolher(e.id, rotulo); setAberta(false); }}>
                      {rotulo}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button type="button" className="ligacao" onClick={() => setAberta(false)}>Fechar a busca</button>
        </div>
      )}
    </div>
  );
}
